import _ from 'lodash';
import cesTrack from '@mxplatform/ces-track';

const TRACKING_TYPES = {
  ROUTE:  'route',
  ACTION: 'action',
};
TRACKING_TYPES.ALL = _.values(TRACKING_TYPES);

const EXIT_PAGE_TYPES = {
  NAVIGATION: 'Navigation',
  CLOSE:      'Close',
};

const EVENT_NAMES = {
  AREA_EXITED: 'Area Exited',
  PAGE_VIEWED: 'Page Viewed',
};

const SEGMENT_TRACK_URL = 'https://api.segment.io/v1/track';

// Variables to hold current and saved page state for duration tracking
let currentPageData = null;
let savedPageData = null;

// Visibility tracking state - tracks only time when page is actually visible to user
let visibleSince = document.visibilityState === 'visible' ? performance.now() : null;
let totalVisibleTime = 0;

// Formats page name segments into a readable string
const formatSegments = (segments) => {
  if (Array.isArray(segments)) return segments.join(' => ');
  return segments;
};

// Checks if the analytics library (Segment) is loaded and available
// Must be a function (not a constant) because Segment loads asynchronously via cesTrack.init()
// and window.analytics is not available until after the script finishes loading
// When a methodName is provided, also verifies that window.analytics[methodName] is a function
const isAnalyticsAvailable = (methodName) =>
  Boolean(window.analytics) && (!methodName || typeof window.analytics[methodName] === 'function');

/**
 * Calculates total visible duration including any current visible session
 * Returns only the time user actually had the page visible (tab active)
 * Uses performance.now() for monotonic timing, rounded to integer milliseconds
 */
const getVisibleDuration = () => {
  if (visibleSince !== null) {
    return Math.round(totalVisibleTime + (performance.now() - visibleSince));
  }
  return Math.round(totalVisibleTime);
};

/**
 * Sends an "Area Exited" event with visible duration for the current page
 * Only counts time when the page was actually visible to the user
 */
const trackPageClose = (exitType) => {
  if (!isAnalyticsAvailable('track')) return;
  if (!currentPageData) return;

  window.analytics.track(EVENT_NAMES.AREA_EXITED, {
    area:       formatSegments(currentPageData.segments),
    title:      currentPageData.title,
    durationMs: getVisibleDuration(),
    path:       currentPageData.path,
    url:        currentPageData.url,
    exitType,
  });
};

/**
 * Sends page close event using navigator.sendBeacon() directly to Segment HTTP API
 * This is used during page unload (pagehide) because regular analytics.track() uses XHR
 * which gets cancelled by the browser during page close.
 * sendBeacon is guaranteed to be queued for delivery even during page unload.
 */
const trackPageCloseViaBeacon = () => {
  if (!isAnalyticsAvailable('user')) return;
  if (!currentPageData) return;

  const writeKey = App.globalContext.cesTrackSegmentWriteKey;

  const user = window.analytics.user();
  const userId = user.id();
  const anonymousId = user.anonymousId();

  if (!userId && !anonymousId) return;

  const payload = {
    userId,
    anonymousId,
    event:      EVENT_NAMES.AREA_EXITED,
    properties: {
      area:       formatSegments(currentPageData.segments),
      title:      currentPageData.title,
      durationMs: getVisibleDuration(),
      path:       currentPageData.path,
      url:        currentPageData.url,
      exitType:   EXIT_PAGE_TYPES.CLOSE,
    },
    timestamp: new Date().toISOString(),
    writeKey,
  };

  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
  navigator.sendBeacon(SEGMENT_TRACK_URL, blob);
};

const onVisible = () => {
  visibleSince = performance.now();
};

const onHidden = () => {
  totalVisibleTime += performance.now() - visibleSince;
  visibleSince = null;
};

/**
 * Handles visibility change events to track actual viewing time
 * When hidden: accumulates visible time, when visible: starts new timing session
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    onVisible();
  } else if (visibleSince !== null) {
    onHidden();
  }
};

/**
 * Tracks a page view and sends exit event for the previous page
 * Called on route changes to record navigation analytics
 */
const trackPage = ({ pageNameSegments, type, route }) => {
  if (!isAnalyticsAvailable('page')) return;

  trackPageClose(EXIT_PAGE_TYPES.NAVIGATION);

  if (pageNameSegments) {
    window.analytics.page(EVENT_NAMES.PAGE_VIEWED, {
      area: formatSegments(pageNameSegments),
    });

    totalVisibleTime = 0;
    visibleSince = document.visibilityState === 'visible' ? performance.now() : null;

    currentPageData = {
      segments: pageNameSegments,
      path:     route,
      title:    document.title,
      url:      window.location.href,
    };
  }
};

/**
 * Saves the current page state before opening a modal
 * Allows restoring and re-tracking the page when the modal closes
 */
const saveCurrentPageState = () => {
  if (!isAnalyticsAvailable()) return;
  if (currentPageData) savedPageData = { ...currentPageData };
};

/**
 * Restores and re-tracks the saved page state after closing a modal
 * Re-triggers trackPage to simulate returning to the previous page
 */
const restoreSavedPageState = () => {
  if (!isAnalyticsAvailable()) return;
  if (!savedPageData) return;

  trackPage({
    pageNameSegments: savedPageData.segments,
    type:             TRACKING_TYPES.ROUTE,
    route:            savedPageData.path,
  });

  savedPageData = null;
};

/**
 * Track an action - requires developers to set up a specific tracked action
 * @eventName String - a simple human-readable String to describe the event being tracked
 * @eventPayload Object - a simple non-nested object that contains meaningful tracked data
 */
const trackAction = (eventName, eventPayload) => {
  if (isAnalyticsAvailable('track')) {
    window.analytics.track(eventName, eventPayload);
  }
};

// Only initialize tracking if segment write key is configured
if (App.globalContext.cesTrackSegmentWriteKey) {
  const { user, account } = App.globalContext;

  cesTrack.init({
    segmentWriteKey: App.globalContext.cesTrackSegmentWriteKey,
    identifiable:    {
      email:      user.email,
      name:       user.name,
      customerId: account.id,
      org:        `${account.name} - ${account.id}`,
      bu:         account.topHierarchyId || '',
      buName:     account.topHierarchyAccountName,
      vertical:   account.vertical,
      role:       user.role.features.mainRoleName,
    },
    appInfo: {
      name:         App.globalContext.cesTrackAppName,
      version:      App.globalContext.ngxCoreappVersion,
      serverRegion: App.globalContext.serverURLsecure,
    },
    settings: {
      autoPage: false,
    },
  });

  // Setup page close and visibility tracking
  // Using sendBeacon for pagehide ensures the event is delivered even during page unload
  // Regular analytics.track() uses XHR which gets cancelled by browser during page close
  window.addEventListener('pagehide', () => {
    trackPageCloseViaBeacon();
  });
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export default {
  trackingTypes: TRACKING_TYPES,
  trackPage,
  trackPageClose,
  trackAction,
  saveCurrentPageState,
  restoreSavedPageState,
};
